import { createSlice } from '@reduxjs/toolkit';

import { commonService } from '../../services/commonServiceInstance';
import { logger } from '../../helpers/logger';
import { PortalTypes } from '../../enums/PortalTypes';

import Cookies from 'js-cookie';
import queryString from 'query-string';
import { differenceInMinutes } from 'date-fns';
import {
    checkValidTokenExists,
    getCookieDomain,
    getJwtTokenExpireMillis,
    setCookieToken,
    tokenResolver,
} from '../../helpers/tokens';
import { handleApiCall } from '../../helpers/handleApiCall';
import UnauthorizedError from '../../services/UnauthorizedError';
import { AuthState, AuthenticationFailureReason, BroadcastEventType } from '../../enums';
import { broadcastChannel } from '../../broadcast/BroadcastChannel';
import { getAppInsightsInstance } from '../../components/AppInsightsProvider/appInsights';
import { navigationService } from '../../routing/navigationService';
import { forceSessionReplay } from '../../helpers/sessionReplay';

// central location for idle time constants
export const IdleConstants = {
    RefreshToken: {
        IDLE_MINUTES: 5,
    },
    AutoLogout: {
        IDLE_MINUTES: 12,
        IDLE_TIME_REFRESH_THRESHOLD: 5,
        IDLE_CHECK_INTERVAL_SECONDS: 30,
    },
};

// Set the portalService Instance From each portals store
let portalService;
export const setService = (service, configureCommon) => {
    service.setHost(window.environment.API_URL);
    service.setHistory(navigationService);
    commonService.setHistory(navigationService);
    commonService.setHost(window.environment.API_URL);
    if (configureCommon) configureCommon(commonService);

    // set up token resolver to fetch token from cookie (single source of truth)
    [service, commonService].forEach((s) => s.setTokenResolver(tokenResolver));

    portalService = service;
};

// Determine current portal
const portalType = PortalTypes.getPortalType();
const portal = window.environment.PORTAL_NAME;

// Portal Cookie logic
const portalKey = `Portal${window.environment.ENVIRONMENT}`;
const params = queryString.parse(window.location.search);
const shouldIgnorePortalCookie = params?.ignorePortalCookie === 'true' || false;

if (!shouldIgnorePortalCookie) {
    const domain = getCookieDomain();
    Cookies.set(portalKey, portal, { domain });
}

const initialState = {
    isLoggedIn: false,
    isAuthenticated: false,
    allocatedClientIds: [],
    token: tokenResolver(), // Initialize token from tokenResolver (cookie)
    isRunningHealthCheck: false,
    appDetails: {},
    reSubscribeTrigger: 0,
    isLoggingOut: false,
};

const slice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setToken(state, action) {
            const token = action.payload;
            state.token = token;
            setCookieToken(token);
        },
        setTokenRefreshHandler(state, action) {
            state.tokenRefreshHandler = action.payload;
        },
        setUser(state, action) {
            const user = action.payload;
            state.user = user || {};
            state.allocatedClientIds = user?.clients?.map((client) => client.clientId) || [];
            state.isAuthenticated = true;
        },
        setPermissions(state, action) {
            state.permissions = action.payload;
        },
        setHealthcareOrganization(state, action) {
            state.healthcareOrganization = action.payload;
        },
        setClientOrganization(state, action) {
            state.clientOrganization = action.payload;
        },
        setFeatures(state, action) {
            state.features = action.payload;
        },
        setReleaseNotes(state, action) {
            state.releaseNotes = action.payload;
        },
        setPrivacyPolicy(state, action) {
            state.privacyPolicy = action.payload;
        },
        setTermsOfUse(state, action) {
            state.termsOfUse = action.payload;
        },
        setConfig(state, action) {
            state.config = action.payload;
        },
        clearAuthenticationFailure(state) {
            state.authenticationFailure = null;
        },
        setAuthenticationFailure(state, action) {
            // this is to prevent future failures from overwriting the original message
            if (!state.authenticationFailure) state.authenticationFailure = action.payload;
            // auth error is a network issue or a temporary auth endpoint failure, so don't
            // clear out user
            if (state.authenticationFailure === AuthState.AuthenticationError) return;
            state.user = undefined;
            state.allocatedClientIds = [];
            state.isAuthenticated = false;
        },
        clearContext(state, action) {
            const authenticationFailureReason = action.payload;
            state.token = undefined;
            state.user = undefined;
            state.allocatedClientIds = [];
            state.permissions = undefined;
            state.isAuthenticated = false;
            state.authenticationFailure = authenticationFailureReason;
        },
        setIsRunningHealthCheck(state, action) {
            state.isRunningHealthCheck = action.payload;
        },
        setAppDetails(state, action) {
            state.appDetails = action.payload;
        },
        setReSubscribeTrigger(state, action) {
            state.reSubscribeTrigger = action.payload;
        },
        setIsLoggingOut(state, action) {
            state.isLoggingOut = action.payload;
        },
    },
});

export const {
    setTokenRefreshHandler,
    setToken,
    setUser,
    clearContext,
    setPermissions,
    clearAuthenticationFailure,
    setAuthenticationFailure,
    setHealthcareOrganization,
    setClientOrganization,
    setFeatures,
    setReleaseNotes,
    setPrivacyPolicy,
    setTermsOfUse,
    setConfig,
    setIsRunningHealthCheck,
    setAppDetails,
    setReSubscribeTrigger,
    setIsLoggingOut,
} = slice.actions;
export const portalAuth = { reducer: slice.reducer, initialState };
export default portalAuth;

const broadcastTokenRefreshed = () => {
    broadcastChannel.postMessage({
        type: BroadcastEventType.TokenRefreshed,
    });
};

const createReSubscribeTrigger = () => Date.now();

// Thunks (side effects)

export const getContext = (isInitialLogin) => async (dispatch, getState) => {
    try {
        const {
            user,
            permissions,
            healthcareOrganization,
            clientOrganization,
            features,
            releaseNotes,
            privacyPolicy,
            termsOfUse,
            config,
            hostname,
            apiVersion,
            cluster,
            forceSessionReplay: shouldForceSessionReplay,
        } = await commonService.getContext({ portalType });

        if (shouldForceSessionReplay) await forceSessionReplay();

        dispatch(setUser(user));

        dispatch(setPermissions(permissions));
        dispatch(setHealthcareOrganization(healthcareOrganization));
        dispatch(setClientOrganization(clientOrganization));
        dispatch(setFeatures(features ? features[PortalTypes.getFeatureKey(portal)] : null));
        dispatch(setReleaseNotes(releaseNotes));
        dispatch(setPrivacyPolicy(privacyPolicy));
        dispatch(setTermsOfUse(termsOfUse));
        dispatch(setConfig(config));
        dispatch(setAppDetails({ hostname, apiVersion, cluster }));
        // tell other tabs to login
        if (isInitialLogin && user) {
            broadcastChannel.postMessage({
                type: BroadcastEventType.Login,
                payload: { userId: user.userId },
            });
        }
        // set the user in app insights
        const appInsights = getAppInsightsInstance();
        if (appInsights && !appInsights.user) {
            appInsights.user = user;
        }
    } catch (err) {
        await dispatch(handleAuthenticationFailure(err));
    }
};

export const healthcheck = () => async (dispatch, getState) => {
    await dispatch(setIsRunningHealthCheck(true));
    try {
        await commonService.healthcheck();
        await dispatch(clearAuthenticationFailure());
        await dispatch(setIsRunningHealthCheck(false));
        return true;
    } catch (err) {
        await dispatch(setIsRunningHealthCheck(false));
        return false;
    }
};

export const healthcheckConfirm = () => async (dispatch, getState) => {
    // bypasses the usual pipeline to prevent infinite calls where a healthcheck
    // triggers a network error which triggers a healthcheck, etc
    const { response, error } = await commonService.healthcheckSlim();
    const isHealthy = response && !error;
    return isHealthy;
};

export const logout = (reason, isFromBroadcast) => async (dispatch, getState) => {
    // if isFromBroadcast is true the logout was already done on another tab
    const isLoggingOut = getState().auth?.isLoggingOut;

    if (!isLoggingOut) {
        await dispatch(setIsLoggingOut(true));
    }

    if (!isFromBroadcast) {
        try {
            // make background AJAX call to SSO logout. This will expire the cookie token.
            await commonService.logout();
        } catch (err) {
            logger.error('SSO logout failed.', err);
        }
    }

    dispatch(clearContext(reason));
    dispatch(cancelQueuedRefreshToken());
    dispatch(setIsLoggingOut(false));

    // tell other tabs to logout
    broadcastChannel.postMessage({
        type: BroadcastEventType.SessionExpired,
        payload: { reason },
    });

    // clear the user in app insights
    const appInsights = getAppInsightsInstance();
    if (appInsights) appInsights.user = null;
};

export const refreshToken = () => async (dispatch) => {
    try {
        if (!checkValidTokenExists()) {
            logger.debug('refreshToken was called but a valid token does not exist. Aborting.');
            return;
        }

        const { token } = await commonService.refreshToken();
        dispatch(setToken(token));
        dispatch(setReSubscribeTrigger(createReSubscribeTrigger()));
        broadcastTokenRefreshed();
        dispatch(queueRefreshToken());
    } catch (err) {
        await dispatch(handleAuthenticationFailure(err));
    }
};

export const queueRefreshToken = () => async (dispatch, getState) => {
    const cookieToken = tokenResolver(); // get current token from cookie so we can determine timeout millis
    const timeoutMillis = getJwtTokenExpireMillis(cookieToken) - 60000; // 1min before expire

    //clear the existing setTimeout handle by stored id
    await dispatch(cancelQueuedRefreshToken());

    const handler = setTimeout(async () => {
        const { autoLogoutDialogIsVisible } = getState().general;
        const lastActiveTime = localStorage.getItem('lastActiveTime');
        const minutesIdle = differenceInMinutes(new Date(), new Date(lastActiveTime));

        // prevents token from refreshing if dialog is showing or the user is past the auto-logout threshold
        if (autoLogoutDialogIsVisible || minutesIdle >= IdleConstants.AutoLogout.IDLE_MINUTES)
            return;

        if (!checkValidTokenExists()) {
            logger.debug(
                'queueRefreshToken.refreshToken was called but a valid token does not exist. Aborting.',
            );
            return;
        }

        try {
            const { token } = await commonService.refreshToken();
            dispatch(setToken(token));
            dispatch(setReSubscribeTrigger(createReSubscribeTrigger()));
            broadcastTokenRefreshed();
            dispatch(queueRefreshToken());
            dispatch(getContext());
        } catch (err) {
            await dispatch(handleAuthenticationFailure(err));
        }
    }, timeoutMillis);

    // Save timeout handler for future use if we need to cancel
    dispatch(setTokenRefreshHandler(handler));
};

export const cancelQueuedRefreshToken = () => async (dispatch, getState) => {
    const { tokenRefreshHandler } = getState().auth;
    clearTimeout(tokenRefreshHandler);
};

export const generatePassword = () => async (dispatch) => {
    const { wasSuccessful, password } = await handleApiCall(
        async () => await commonService.generatePassword(),
        {
            dispatch,
        },
    );
    if (!wasSuccessful) return;
    return password;
};

const handleAuthenticationFailure = (err) => async (dispatch, getState) => {
    // this is handled by the API event bus
    if (err instanceof UnauthorizedError) return;
    await dispatch(setAuthenticationFailure(AuthenticationFailureReason.Error));
};
